import type { Point2D } from '@shared/models/geometry.model';
import { roundToDecimals } from '@shared/helpers/math.helpers';

/** World-space angle increment for the add-building pen (DEV-2576). */
const PEN_SNAP_ANGLE_DEGREES = 5;

/** Length decimals so pen segments snap to 0.1 m. */
const PEN_SNAP_LENGTH_DECIMALS = 1;

/** Angle increment used by Shift while drawing a custom footprint. */
const CUSTOM_DRAFT_ANGLE_INCREMENT_DEGREES = 45;

/**
 * A pitched camera can place a screen-axis point somewhat past the pointer.
 * The factor still rejects a horizon collapse hundreds of meters away.
 */
const SCREEN_AXIS_REACH_FACTOR = 4;
const SCREEN_AXIS_EXTRA_METERS = 40;

const CUSTOM_DRAFT_SCREEN_ANGLES_DEGREES = [0, 45, 90, 135] as const;

/** Snap a non-zero draft segment to the nearest 0.1 metre. */
export const snapDraftSegmentLength = (
  origin: Point2D,
  pointer: Point2D,
): Point2D => {
  const dx = pointer[0] - origin[0];
  const dy = pointer[1] - origin[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) return origin;

  const snappedLength = roundToDecimals(length, PEN_SNAP_LENGTH_DECIMALS);
  const scale = snappedLength / length;
  return [origin[0] + dx * scale, origin[1] + dy * scale];
};

/**
 * Snap a pen-tool draft point to a 5° world angle and 0.1 m length from
 * {@link origin}. Empty-design vertex editing keeps the global orthogonal snap;
 * this helper is pen-mode only.
 * A zero-length segment stays at {@link origin}.
 */
export const snapPenDraftPoint = (
  origin: Point2D,
  pointer: Point2D,
): Point2D => {
  const dx = pointer[0] - origin[0];
  const dy = pointer[1] - origin[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) return origin;

  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const snappedDeg =
    roundToDecimals(angleDeg / PEN_SNAP_ANGLE_DEGREES, 0) *
    PEN_SNAP_ANGLE_DEGREES;
  const snappedLength = roundToDecimals(length, PEN_SNAP_LENGTH_DECIMALS);
  const snappedRad = (snappedDeg * Math.PI) / 180;

  return [
    origin[0] + Math.cos(snappedRad) * snappedLength,
    origin[1] + Math.sin(snappedRad) * snappedLength,
  ];
};

/**
 * For the first custom-footprint segment, choose the closest 0°/45°/90°/135°
 * screen line through the last point.
 */
export const snapDraftPointToScreenAngle = (input: {
  pointerScreen: Point2D;
  lastScreen: Point2D;
  screenToMetric: (point: Point2D) => Point2D | undefined;
}): Point2D => {
  const pointerOffset: Point2D = [
    input.pointerScreen[0] - input.lastScreen[0],
    input.pointerScreen[1] - input.lastScreen[1],
  ];
  const candidates = CUSTOM_DRAFT_SCREEN_ANGLES_DEGREES.flatMap((degrees) => {
    const radians = (degrees * Math.PI) / 180;
    const direction: Point2D = [Math.cos(radians), Math.sin(radians)];
    const projectedDistance =
      pointerOffset[0] * direction[0] + pointerOffset[1] * direction[1];
    const screenPoint: Point2D = [
      input.lastScreen[0] + direction[0] * projectedDistance,
      input.lastScreen[1] + direction[1] * projectedDistance,
    ];
    const metricPoint = input.screenToMetric(screenPoint);
    return metricPoint ? [{ metricPoint, screenPoint }] : [];
  });

  return candidates.reduce(
    (closest, candidate) => {
      const distance = Math.hypot(
        candidate.screenPoint[0] - input.pointerScreen[0],
        candidate.screenPoint[1] - input.pointerScreen[1],
      );
      return distance < closest.distance
        ? { point: candidate.metricPoint, distance }
        : closest;
    },
    {
      point: input.screenToMetric(input.pointerScreen) ?? ([0, 0] as Point2D),
      distance: Number.POSITIVE_INFINITY,
    },
  ).point;
};

/** Snap the outgoing angle relative to the previous segment in 45° steps. */
export const snapDraftPointToPreviousSegmentAngle = (input: {
  pointerMetric: Point2D;
  lastMetric: Point2D;
  previousMetric: Point2D;
}): Point2D => {
  const incomingAngle = Math.atan2(
    input.lastMetric[1] - input.previousMetric[1],
    input.lastMetric[0] - input.previousMetric[0],
  );
  const pointerX = input.pointerMetric[0] - input.lastMetric[0];
  const pointerY = input.pointerMetric[1] - input.lastMetric[1];
  const pointerLength = Math.hypot(pointerX, pointerY);
  if (pointerLength === 0) return input.lastMetric;

  const pointerAngle = Math.atan2(pointerY, pointerX);
  const incrementRadians =
    (CUSTOM_DRAFT_ANGLE_INCREMENT_DEGREES * Math.PI) / 180;
  const snappedRelativeAngle =
    Math.round((pointerAngle - incomingAngle) / incrementRadians) *
    incrementRadians;
  const snappedAngle = incomingAngle + snappedRelativeAngle;
  return [
    input.lastMetric[0] + Math.cos(snappedAngle) * pointerLength,
    input.lastMetric[1] + Math.sin(snappedAngle) * pointerLength,
  ];
};

/** Existing Shift behavior for polygon drawing outside New building / Custom. */
export const snapDraftPointToScreenOrRightAngle = (input: {
  pointerMetric: Point2D;
  pointerScreen: Point2D;
  lastMetric: Point2D;
  lastScreen: Point2D;
  previousMetric?: Point2D;
  screenToMetric: (point: Point2D) => Point2D | undefined;
  metricToScreen: (point: Point2D) => Point2D | undefined;
}): Point2D => {
  const pointerReach = Math.hypot(
    input.pointerMetric[0] - input.lastMetric[0],
    input.pointerMetric[1] - input.lastMetric[1],
  );
  // Screen projection can collapse a horizon point onto the pointer. A pitched
  // axis may sit a few dozen meters past the pointer; a collapse of hundreds
  // of meters may not.
  const isBesidePointer = (candidate: Point2D): boolean => {
    const fromPointer = Math.hypot(
      candidate[0] - input.pointerMetric[0],
      candidate[1] - input.pointerMetric[1],
    );
    return (
      fromPointer <=
      Math.max(
        pointerReach * SCREEN_AXIS_REACH_FACTOR,
        SCREEN_AXIS_EXTRA_METERS,
      )
    );
  };
  const candidates = [
    input.screenToMetric([input.pointerScreen[0], input.lastScreen[1]]),
    input.screenToMetric([input.lastScreen[0], input.pointerScreen[1]]),
  ].filter(
    (candidate): candidate is Point2D =>
      candidate !== undefined && isBesidePointer(candidate),
  );

  if (input.previousMetric) {
    const incomingX = input.lastMetric[0] - input.previousMetric[0];
    const incomingY = input.lastMetric[1] - input.previousMetric[1];
    const squaredLength = incomingX ** 2 + incomingY ** 2;
    if (squaredLength > 1e-9) {
      const perpendicularX = -incomingY;
      const perpendicularY = incomingX;
      const pointerX = input.pointerMetric[0] - input.lastMetric[0];
      const pointerY = input.pointerMetric[1] - input.lastMetric[1];
      const ratio =
        (pointerX * perpendicularX + pointerY * perpendicularY) / squaredLength;
      const rightAngle: Point2D = [
        input.lastMetric[0] + perpendicularX * ratio,
        input.lastMetric[1] + perpendicularY * ratio,
      ];
      if (isBesidePointer(rightAngle)) candidates.push(rightAngle);
    }
  }

  return candidates.reduce(
    (closest, candidate) => {
      const candidateScreen = input.metricToScreen(candidate);
      if (!candidateScreen) return closest;
      const distance = Math.hypot(
        candidateScreen[0] - input.pointerScreen[0],
        candidateScreen[1] - input.pointerScreen[1],
      );
      return distance < closest.distance ? { candidate, distance } : closest;
    },
    {
      candidate: input.pointerMetric,
      distance: Number.POSITIVE_INFINITY,
    },
  ).candidate;
};
